import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MBWStageDownload, StageComponentInterface } from '../../../../../interfaces/pagebuilder';
import { generatePdfThumbnail } from '../../../../../services/services';
import VyButton from '../../../../common/VyButton';
import VyTextField from '../../../../common/VyTextField';
import VyUpload from '../../../../common/VyUpload';

interface MBWDownloadFormProps {
  component: StageComponentInterface;
  currentDownload?: MBWStageDownload;
  onChange: (c: StageComponentInterface) => void;
  onSubmit?: () => void;
}

const MBWDownloadForm = ({ component, currentDownload, onChange, onSubmit }: MBWDownloadFormProps) => {
  const { handleSubmit, control, reset, setValue, watch } = useForm<MBWStageDownload>({ mode: 'onChange' });
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentDownload) reset(currentDownload);
  }, [currentDownload]);

  function handleDownloadCreate(props: MBWStageDownload) {
    const currentComponent = { ...component };
    currentComponent.downloads = [...(currentComponent.downloads || []), props];
    onChange(currentComponent);
    // reset not working on VyUpload so we use setValue
    setValue('thumbnail', null);
    setValue('file', null);
    reset();
  }
  function handleDownloadEdit(download: MBWStageDownload) {
    const currentComponent = { ...component };
    currentComponent.downloads = currentComponent.downloads?.map((d) => {
      if (d._id === currentDownload?._id) return download;
      return d;
    });
    onChange(currentComponent);
    onSubmit?.();
  }
  return (
    <>
      <form onSubmit={handleSubmit(currentDownload ? handleDownloadEdit : handleDownloadCreate)} className="flex flex-col p-4 mt-4 space-y-2">
        <Controller
          control={control}
          name={`title`}
          rules={{ required: true }}
          render={({ field }) => <VyTextField {...field} fullWidth required label={t('pagebuilder:title')} />}
        />
        <Controller control={control} name={`description`} render={({ field }) => <VyTextField {...field} fullWidth label={t('pagebuilder:description')} />} />
        <span>{t('pagebuilder:file')}*</span>
        <Controller
          control={control}
          name="file"
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <VyUpload
              acceptedFileTypes="*"
              randomExternalImageButton={false}
              defaultFileId={value}
              onChange={(fileId, fileType) => {
                onChange(fileId);

                if (fileType === 'application/pdf' && fileId) {
                  setLoading(true);
                  generatePdfThumbnail(fileId)
                    .then((x) => {
                      setValue('thumbnail', x);
                    })
                    .catch((err) => console.error(err))
                    .finally(() => setLoading(false));
                }
              }}
            />
          )}
        />
        <span> {t('pagebuilder:video-thumbnail')}</span>
        <Controller
          control={control}
          name="thumbnail"
          render={({ field: { onChange, value } }) => (
            <VyUpload
              acceptedFileTypes="image/*"
              randomExternalImageButton={false}
              defaultFileId={value}
              cropSize={{ width: 300, height: 500 }}
              onChange={(fileId) => {
                onChange(fileId);
              }}
            />
          )}
        />
        <VyButton
          disabled={!watch('file') || !watch('title') || loading}
          showLoadingIcon={loading}
          loadingText={t('account:generating-thumbnail')}
          style={{ marginTop: '1rem' }}
          type="submit">
          {t('account:confirm')}
        </VyButton>
      </form>
    </>
  );
};

export default MBWDownloadForm;
